import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, mediaUrl, isHostedVideo, fetchIngredientImages, fetchUtensilImages } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { startCheckout } from "@/lib/checkout";
import {
  Play, ChevronDown, Heart, Bookmark, Minus, Plus, Users, Check, Clock,
  CalendarPlus, Leaf, Fish, Milk, Wheat, Flame, Snowflake, Croissant, Utensils, CookingPot,
} from "lucide-react";
import { toast } from "sonner";
import { ShareIcon } from "@/components/ShareIcon";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";

const isoDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const MEALS = ["Breakfast", "Lunch", "Dinner", "Snack"];

const AISLE = {
  "Produce": { icon: Leaf, tint: "bg-black/5 text-nerd-light" },
  "Meat & Seafood": { icon: Fish, tint: "bg-coral/15 text-coral" },
  "Dairy": { icon: Milk, tint: "bg-sky/15 text-sky" },
  "Pantry": { icon: Wheat, tint: "bg-sand/15 text-sand" },
  "Spices": { icon: Flame, tint: "bg-nerd-light/15 text-orchid" },
  "Bakery": { icon: Croissant, tint: "bg-sand/15 text-sand" },
  "Frozen": { icon: Snowflake, tint: "bg-sky/15 text-sky" },
  "Other": { icon: Utensils, tint: "bg-black/8 text-nerd-light/70" },
};

const fmt = (n) => (Number.isInteger(n) ? n : parseFloat(n.toFixed(2)));

function TickRow({ testid, tile, title, sub, checked, onToggle }) {
  return (
    <button
      data-testid={testid}
      onClick={onToggle}
      className={`w-full flex items-center gap-4 p-3 rounded-2xl border transition-colors text-left ${
        checked ? "bg-nerd-card/40 border-black/5" : "bg-nerd-card border-black/8 hover:border-black/15"
      }`}
    >
      {tile}
      <div className="flex-1 min-w-0">
        <p className={`font-medium truncate ${checked ? "line-through text-nerd-light/40" : "text-nerd-light"}`}>{title}</p>
        {sub && <p className={`text-sm ${checked ? "text-nerd-light/25" : "text-nerd-light/50"}`}>{sub}</p>}
      </div>
      <span className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
        checked ? "bg-nerd-light border-2 border-nerd-light text-white" : "border-2 border-nerd-light/50 text-transparent"
      }`}>
        <Check className="w-4 h-4" strokeWidth={3} />
      </span>
    </button>
  );
}

function NutritionCard({ macros, servingSize }) {
  const DV = { fat: 78, sat: 20, sodium: 2300, carbs: 275, fiber: 28, protein: 50 };
  const pct = (v, d) => Math.round((v / d) * 100);
  const Row = ({ label, value, dv, indent, last }) => (
    <div className={`flex items-center justify-between py-1.5 ${last ? "" : "border-b border-black/10"} ${indent ? "pl-4" : ""}`}>
      <span className="text-nerd-bg/70 text-sm">{label} <span className="text-nerd-bg font-medium">{value}</span></span>
      {dv != null && <span className="text-nerd-bg/60 text-sm font-medium">{dv}%</span>}
    </div>
  );
  return (
    <div className="bg-[#f5f5f5] border border-black/10 rounded-2xl p-5 text-nerd-bg">
      <div className="flex items-baseline justify-between">
        <div>
          <h3 className="font-display text-lg font-extrabold tracking-tight text-nerd-bg">Nutrition Estimates</h3>
          <p className="text-nerd-bg/40 text-xs mt-0.5">Per serving • {servingSize || "1 serving"}</p>
        </div>
        <div className="text-right">
          <span className="font-display text-3xl font-extrabold leading-none text-nerd-bg">{macros.calories}</span>
          <p className="text-nerd-bg/40 text-[11px] mt-0.5">calories · % DV</p>
        </div>
      </div>
      <div className="h-px bg-black/15 my-3" />
      <Row label="Total Fat" value={`${macros.fat_g}g`} dv={pct(macros.fat_g, DV.fat)} />
      {macros.saturated_fat_g > 0 && <Row label="Saturated Fat" value={`${macros.saturated_fat_g}g`} dv={pct(macros.saturated_fat_g, DV.sat)} indent />}
      {macros.sodium_mg > 0 && <Row label="Sodium" value={`${macros.sodium_mg}mg`} dv={pct(macros.sodium_mg, DV.sodium)} />}
      <Row label="Total Carbohydrate" value={`${macros.carbs_g}g`} dv={pct(macros.carbs_g, DV.carbs)} />
      {macros.fiber_g > 0 && <Row label="Dietary Fiber" value={`${macros.fiber_g}g`} dv={pct(macros.fiber_g, DV.fiber)} indent />}
      {macros.sugars_g > 0 && <Row label="Total Sugars" value={`${macros.sugars_g}g`} indent />}
      <Row label="Protein" value={`${macros.protein_g}g`} dv={pct(macros.protein_g, DV.protein)} last />
    </div>
  );
}

export default function RecipeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [recipe, setRecipe] = useState(null);
  const [servings, setServings] = useState(1);
  const [saved, setSaved] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);
  const [pantryNames, setPantryNames] = useState([]);
  const [ticks, setTicks] = useState(() => new Set());
  const [open, setOpen] = useState(false);
  const [planDate, setPlanDate] = useState(new Date());
  const [meal, setMeal] = useState("Dinner");
  const [adding, setAdding] = useState(false);
  const [ingImages, setIngImages] = useState({});
  const [utImages, setUtImages] = useState({});

  useEffect(() => {
    api.get(`/recipes/${id}`).then((r) => { setRecipe(r.data); setServings(r.data.servings || 1); })
      .catch(() => toast.error("Recipe not found"));
    try {
      const raw = localStorage.getItem(`fi_ticks_${id}`);
      if (raw) setTicks(new Set(JSON.parse(raw)));
    } catch {}
  }, [id]);

  useEffect(() => {
    const names = (recipe?.ingredients || []).map((x) => x.name).filter(Boolean);
    if (names.length) fetchIngredientImages(names).then(setIngImages).catch(() => {});
    const uts = (recipe?.utensils || []).filter(Boolean);
    if (uts.length) fetchUtensilImages(uts).then(setUtImages).catch(() => {});
  }, [recipe]);

  useEffect(() => {
    if (user) {
      api.get("/me/saved").then((r) => setSaved(r.data.some((x) => x.id === id))).catch(() => {});
      api.get("/me/bookmarks").then((r) => setBookmarked(r.data.some((x) => x.id === id))).catch(() => {});
      api.get("/pantry").then((r) => setPantryNames((r.data.items || []).map((i) => (i.name || "").trim().toLowerCase()))).catch(() => {});
    }
  }, [user, id]);

  // Pre-tick ingredients/utensils the user already owns (in their pantry)
  useEffect(() => {
    if (!recipe || pantryNames.length === 0) return;
    setTicks((prev) => {
      const next = new Set(prev);
      (recipe.ingredients || []).forEach((ing, i) => {
        if (pantryNames.includes((ing.name || "").trim().toLowerCase())) next.add(`ing-${i}`);
      });
      (recipe.utensils || []).forEach((u, i) => {
        if (pantryNames.includes((u || "").trim().toLowerCase())) next.add(`ut-${i}`);
      });
      return next;
    });
  }, [recipe, pantryNames]);

  const toggleTick = (key) => {
    setTicks((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      localStorage.setItem(`fi_ticks_${id}`, JSON.stringify([...next]));
      return next;
    });
  };

  const base = recipe?.servings || 1;
  const scale = servings / base;

  const toggleSave = async () => {
    if (!user) { toast.error("Log in to add favourites"); navigate("/login"); return; }
    try {
      const { data } = await api.post(`/recipes/${id}/save`);
      setSaved(data.saved);
      toast.success(data.saved ? "Added to favourites" : "Removed from favourites");
    } catch { toast.error("Could not update"); }
  };

  const toggleBookmark = async () => {
    if (!user) { toast.error("Log in to save recipes"); navigate("/login"); return; }
    try {
      const { data } = await api.post(`/recipes/${id}/bookmark`);
      setBookmarked(data.bookmarked);
      toast.success(data.bookmarked ? "Saved" : "Removed from saved");
    } catch { toast.error("Could not update"); }
  };

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: recipe.title, url });
      else { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
    } catch {}
  };

  const addToPlan = async () => {
    setAdding(true);
    try {
      await api.post("/mealplan/add", { day: isoDate(planDate), meal, recipe_id: id, servings });
      // Persist items the user marked as already having → their pantry
      let tickSet = new Set();
      try { const raw = localStorage.getItem(`fi_ticks_${id}`); if (raw) tickSet = new Set(JSON.parse(raw)); } catch { /* */ }
      const owned = [];
      (recipe.utensils || []).forEach((u, i) => { if (tickSet.has(`ut-${i}`)) owned.push({ name: u, aisle: "Other" }); });
      (recipe.ingredients || []).forEach((ing, i) => {
        if (tickSet.has(`ing-${i}`)) owned.push({ name: ing.name, quantity: ing.quantity || 0, unit: ing.unit || "", aisle: ing.aisle || "Other" });
      });
      if (owned.length) {
        await Promise.all(owned.map((o) => api.post("/pantry/add", o).catch(() => {})));
        setPantryNames((prev) => {
          const s = new Set(prev);
          owned.forEach((o) => s.add((o.name || "").trim().toLowerCase()));
          return [...s];
        });
      }
      toast.success(`Added to ${format(planDate, "EEE, MMM d")} · ${meal} · ${servings} servings`);
      setOpen(false);
    } catch { toast.error("Could not add to planner"); } finally { setAdding(false); }
  };

  const facts = useMemo(() => recipe ? [
    `${recipe.total_time_min} min`,
    `${recipe.ingredients?.length || 0} ingredients`,
    `${recipe.steps?.length || 0} steps`,
  ] : [], [recipe]);

  if (!recipe) return <div className="max-w-3xl mx-auto px-6 py-20 text-nerd-light/40">Loading…</div>;

  const creatorHandle = recipe.creator_handle || `@${(recipe.creator_name || "flavourai").toLowerCase().replace(/\s/g, "")}`;
  const isFI = /flavour\s*intelligence/i.test(recipe.creator_name || "") || /flavourintelligence/i.test(creatorHandle);
  const hasVideo = isHostedVideo(recipe.video_url);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pb-24">
      {/* top bar */}
      <div className="flex items-center justify-between py-4 sm:py-6">
        <button data-testid="back-btn" onClick={() => navigate(-1)}
          className="w-10 h-10 rounded-full bg-nerd-card border border-black/8 flex items-center justify-center text-nerd-light/70 hover:bg-black/5 hover:text-nerd-light transition-colors" title="Back">
          <ChevronDown className="w-4.5 h-4.5 rotate-90" />
        </button>
        <button data-testid="share-btn" onClick={share}
          className="w-10 h-10 rounded-full bg-nerd-card border border-black/8 flex items-center justify-center hover:bg-black/5 transition-colors">
          <ShareIcon size={18} />
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
        {/* LEFT — sticky media + actions */}
        <div className="lg:col-span-5 lg:sticky lg:top-24 lg:self-start space-y-5">
          <div className="relative aspect-[4/5] sm:aspect-[4/3] lg:aspect-[4/5] overflow-hidden rounded-3xl">
            {hasVideo ? (
              <video data-testid="recipe-hero-video" autoPlay loop muted playsInline
                poster={recipe.thumbnail || undefined}
                className="w-full h-full object-cover" src={mediaUrl(recipe.video_url)} />
            ) : (
              <img src={recipe.thumbnail} alt={recipe.title} className="w-full h-full object-cover" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-black/10" />
            <button data-testid="hero-play-btn" onClick={() => navigate(`/cook/${id}`)}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full bg-white flex items-center justify-center text-nerd-light shadow-2xl hover:scale-105 transition-transform">
              <Play className="w-6 h-6 ml-1 fill-current" />
            </button>
          </div>

          {/* servings + actions */}
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <button data-testid="servings-minus" onClick={() => setServings((s) => Math.max(1, s - 1))}
                className="w-11 h-11 rounded-full bg-nerd-card border border-black/8 flex items-center justify-center hover:bg-black/5 transition-colors">
                <Minus className="w-4 h-4" />
              </button>
              <div className="h-11 px-4 rounded-full bg-nerd-card border border-black/8 flex items-center gap-2" data-testid="servings-count">
                <Users className="w-4 h-4 text-nerd-light/60" />
                <span className="font-semibold tabular-nums">{servings}</span>
                <span className="text-nerd-light/40 text-sm">servings</span>
              </div>
              <button data-testid="servings-plus" onClick={() => setServings((s) => s + 1)}
                className="w-11 h-11 rounded-full bg-nerd-card border border-black/8 flex items-center justify-center hover:bg-black/5 transition-colors">
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button data-testid="add-to-plan-btn" onClick={() => { if (!user) { toast.error("Log in to use the planner"); navigate("/login"); return; } if (!user.is_premium) { toast.error("The planner is a Premium feature"); startCheckout(); return; } setOpen(true); }}
                className="w-11 h-11 rounded-full bg-nerd-card border border-black/8 flex items-center justify-center hover:bg-black/5 transition-colors" title="Add to planner">
                <CalendarPlus className="w-4.5 h-4.5" />
              </button>
              <button data-testid="favourite-recipe-toggle" onClick={toggleSave}
                className={`w-11 h-11 rounded-full border flex items-center justify-center transition-colors ${saved ? "bg-black/5 border-black/20 text-nerd-light" : "bg-nerd-card border-black/8 hover:bg-black/5"}`} title="Favourite">
                <Heart className={`w-4.5 h-4.5 ${saved ? "fill-current" : ""}`} />
              </button>
              <button data-testid="save-recipe-toggle" onClick={toggleBookmark}
                className={`w-11 h-11 rounded-full border flex items-center justify-center transition-colors ${bookmarked ? "bg-black/5 border-black/20 text-nerd-light" : "bg-nerd-card border-black/8 hover:bg-black/5"}`} title="Save">
                <Bookmark className={`w-4.5 h-4.5 ${bookmarked ? "fill-current" : ""}`} />
              </button>
            </div>
          </div>
        </div>

        {/* RIGHT — details */}
        <div className="lg:col-span-7 space-y-8 min-w-0">
          <header>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.05] mb-3">{recipe.title}</h1>
            <div data-testid="recipe-creator-tag" style={{ backgroundColor: "#ffffff" }} className="inline-flex items-center gap-2 mb-4 rounded-full pl-1 pr-3.5 py-1 shadow-sm">
              {isFI ? (
                <span className="w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: "rgba(0,0,0,0.06)" }}>
                  <img src="/logo-black.png" alt="" className="w-4 h-4 object-contain" />
                </span>
              ) : (
                <span className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold" style={{ backgroundColor: "#0a0a0a", color: "#ffffff" }}>
                  {(recipe.creator_name || "F").charAt(0)}
                </span>
              )}
              <span className="text-sm font-semibold" style={{ color: "#0a0a0a" }}>{creatorHandle}</span>
            </div>
            <p className="text-nerd-light/60 leading-relaxed max-w-2xl">{recipe.description}</p>
            <div className="flex flex-wrap gap-2 mt-4">
              {facts.map((f) => (
                <span key={f} className="inline-flex items-center gap-1.5 text-sm font-medium text-nerd-light/80 bg-black/5 px-3 py-1.5 rounded-full">{f}</span>
              ))}
            </div>
          </header>

          <NutritionCard macros={recipe.macros || {}} servingSize={recipe.serving_size} />

          {/* Ingredients */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <h2 className="font-display text-2xl font-extrabold tracking-tight">Ingredients</h2>
              <span className="w-7 h-7 rounded-full bg-nerd-light text-white text-xs font-bold flex items-center justify-center">{recipe.ingredients?.length || 0}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-testid="ingredient-list">
              {(recipe.ingredients || []).map((ing, i) => {
                const meta = AISLE[ing.aisle] || AISLE.Other;
                const Icon = meta.icon;
                const key = `ing-${i}`;
                const imgUrl = ingImages[(ing.name || "").trim().toLowerCase()];
                return (
                  <TickRow
                    key={i}
                    testid={`ingredient-${i}`}
                    tile={imgUrl
                      ? <div className="shrink-0 w-14 h-14 rounded-2xl overflow-hidden bg-nerd-lift"><img src={mediaUrl(imgUrl)} alt={ing.name} className="w-full h-full object-cover" /></div>
                      : <div className={`shrink-0 w-14 h-14 rounded-2xl flex items-center justify-center ${meta.tint}`}><Icon className="w-6 h-6" /></div>}
                    title={ing.name}
                    sub={`${fmt((ing.quantity || 0) * scale)} ${ing.unit}`.trim()}
                    checked={ticks.has(key)}
                    onToggle={() => toggleTick(key)}
                  />
                );
              })}
            </div>
          </section>

          {/* Utensils */}
          {(recipe.utensils || []).length > 0 && (
            <section>
              <div className="flex items-center gap-3 mb-4">
                <h2 className="font-display text-2xl font-extrabold tracking-tight">Utensils</h2>
                <span className="w-7 h-7 rounded-full bg-nerd-light text-white text-xs font-bold flex items-center justify-center">{recipe.utensils.length}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-testid="utensil-list">
                {recipe.utensils.map((u, i) => {
                  const key = `ut-${i}`;
                  const utImg = utImages[(u || "").trim().toLowerCase()];
                  return (
                    <TickRow
                      key={i}
                      testid={`utensil-${i}`}
                      tile={utImg
                        ? <div className="shrink-0 w-14 h-14 rounded-2xl overflow-hidden bg-nerd-lift"><img src={mediaUrl(utImg)} alt={u} className="w-full h-full object-cover" /></div>
                        : <div className="shrink-0 w-14 h-14 rounded-2xl bg-black/5 text-nerd-light flex items-center justify-center"><CookingPot className="w-6 h-6" /></div>}
                      title={u}
                      checked={ticks.has(key)}
                      onToggle={() => toggleTick(key)}
                    />
                  );
                })}
              </div>
            </section>
          )}

          {/* Steps */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <h2 className="font-display text-2xl font-extrabold tracking-tight">Steps</h2>
              <span className="w-7 h-7 rounded-full bg-nerd-light text-white text-xs font-bold flex items-center justify-center">{recipe.steps?.length || 0}</span>
            </div>
            <div className="space-y-2.5">
              {(recipe.steps || []).map((s, i) => (
                <div key={i} className="flex gap-4 p-4 rounded-2xl bg-nerd-card border border-black/8">
                  <div className="shrink-0 w-14 h-14 rounded-2xl bg-black/5 text-nerd-light flex items-center justify-center font-mono-x text-lg font-semibold">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="flex-1 pt-0.5">
                    <p className="font-medium text-nerd-light leading-relaxed">{s.instruction}</p>
                    {s.duration_min > 0 && (
                      <p className="text-nerd-light/40 text-sm mt-1.5 flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {s.duration_min} mins</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <button data-testid="cook-now-btn-2" onClick={() => navigate(`/cook/${id}`)}
            className="w-full inline-flex items-center justify-center gap-2 bg-nerd-light text-white font-semibold px-5 py-4 rounded-2xl hover:opacity-90 transition-opacity">
            <Play className="w-5 h-5 fill-current" /> Start cooking
          </button>
        </div>
      </div>

      {/* Add to plan dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="add-plan-dialog" className="bg-nerd-card border-black/10 text-nerd-light">
          <DialogHeader><DialogTitle className="font-display">Add to planner</DialogTitle></DialogHeader>
          <div className="py-2 space-y-4">
            <div className="rounded-2xl bg-nerd-depth border border-black/10 flex justify-center">
              <Calendar mode="single" selected={planDate} onSelect={(d) => d && setPlanDate(d)} data-testid="plan-calendar" />
            </div>
            <div>
              <label className="text-xs font-mono-x uppercase tracking-widest text-nerd-light/50 mb-1.5 block">Meal</label>
              <Select value={meal} onValueChange={setMeal}>
                <SelectTrigger data-testid="plan-meal-select" className="bg-nerd-depth border-black/10"><SelectValue /></SelectTrigger>
                <SelectContent>{MEALS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-sm text-nerd-light/50">Scheduling <span className="text-nerd-light font-semibold">{format(planDate, "EEE, MMM d")}</span> · {servings} servings.</p>
          <DialogFooter>
            <button data-testid="confirm-add-plan-btn" onClick={addToPlan} disabled={adding}
              className="bg-nerd-light text-white px-4 py-2.5 rounded-xl text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-60">
              {adding ? "Adding…" : "Add meal"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
